import { useCallback, useMemo, useRef, useState, useEffect } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { uniq } from 'lodash';
import { QuestionnaireFormProvider, useQuestionnaireContext, useQuestionnaireFormContext } from '@/components/Questionnaire/QuestionnaireProvider';
import removeNullish from '@/helpers/removeNullish';
import HandleWatchers from '../../HandleWatchers';
import { FormFieldProps } from '../../FormField/types';
import ModalFormContent from './ModalFormContent';
import SaveButton from './SaveButton';
import RemoveButton from './RemoveButton';
import ModalWrapper from './ModalWrapper';
import ModalFormHeader from './ModalFormHeader';
import TransferData from './TransferData';
import ModalFormActions from './ModalFormActions';
import s from './EditListItemModal.module.scss';
import getAllChildFields from '../../helpers/getAllChildFields';

type ThisField =
  & Parameters<typeof ModalFormContent>[0]['field']
  & {
    listItemTemplates?: Array<Parameters<typeof ModalFormContent>[0]['field']>;
  };

type Props = {
  field: ThisField;
  linkSourceFieldKey: string;
  onClose: (canRemoveListItem?: boolean) => void;
  onEditField: (otherRowIndex: number) => void;
  onRemove?: (thisRowIndex: number) => void;
  onSave: (newValue: { _id: string }) => void;
  rowIndex: number;
  FormFieldComponent: React.FC<FormFieldProps>;
};

const EditListItemModalV2: React.FC<Props> = ({
  field: initialField,
  linkSourceFieldKey,
  onClose,
  onEditField,
  onRemove,
  onSave,
  rowIndex,
  FormFieldComponent,
}) => {
  const {
    fieldsById,
    fieldsByKey,
    formData,
    isEnabled,
    newListItemId,
  } = useQuestionnaireContext();
  const currentFormContext = useQuestionnaireFormContext();
  const rowFieldKey = `${linkSourceFieldKey}[${rowIndex}]`;
  const parentModalLevel = currentFormContext.modalLevel || 0;

  const modalFieldId = useWatch({
    control: currentFormContext.formMethods.control,
    name: `${linkSourceFieldKey}[${rowIndex}]._id`,
  });

  const modalFormMethods = useForm({
    defaultValues: currentFormContext.formMethods.getValues(),
  });

  const { filterAiAgents, filterAutofills } = useMemo(() => {
    const rowIndexes = new Set<number>([rowIndex]);
    const newFieldsWithAiAgents = new Set<string>();
    const newFieldsWithAutofills = new Set<string>();

    const sourceListField = fieldsByKey[linkSourceFieldKey];
    const allChildFields = getAllChildFields({
      fieldId: sourceListField?.id,
      fieldsById,
    });

    allChildFields.forEach((field) => {
      if (field.autofill) {
        // Below checks will add fields with autofill which has no action to the filterAiAgents and filterAutofills
        if (field.autofill?.action === 'ai-agent' || !field.autofill?.action) {
          newFieldsWithAiAgents.add(field.key);
        }
        if (field.autofill?.action !== 'initial-values' && field.autofill?.action !== 'ai-agent') {
          newFieldsWithAutofills.add(field.key);
        }
      }
    });

    return {
      filterAiAgents: {
        fieldKeys: newFieldsWithAiAgents,
        rowIndexes,
      },
      filterAutofills: {
        fieldKeys: newFieldsWithAutofills,
        rowIndexes,
      },
    };
  }, [fieldsById, fieldsByKey, linkSourceFieldKey, rowIndex]);

  const onSaveRef = useRef(onSave);
  onSaveRef.current = onSave;
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const onRemoveRef = useRef(onRemove);
  onRemoveRef.current = onRemove;

  const [field, setField] = useState(initialField);

  const modalSubFields = useMemo(() => (field.listItemTemplates ?? []).map(subField => ({
    ...subField,
    computedKey: `${rowFieldKey}.${subField.key}`,
    computedLinkedFields: fieldsByKey[subField.key]?.linkedFields?.filter(
      f => f.linkedFieldTemplateIndex === rowIndex,
    ),
    computedListItemTemplates: Object.values(fieldsByKey).filter(
      f => f.listFieldTemplateId === subField.id,
    ),
  })), [
    field.listItemTemplates,
    rowIndex,
    rowFieldKey,
    fieldsByKey,
  ]);

  const [fieldsAutofilling, setFieldsAutoFilling] = useState<string[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [referencedListData, setReferencedListData] = useState<any>(null);
  const [currentListSubsection, setCurrentListSubsection] = useState<string | null>(null);

  const listSubsections = useMemo(() => uniq(modalSubFields
    .map((subField) => subField.listSubsection)
    .filter(removeNullish),
  ), [modalSubFields]);

  useEffect(() => {
    setCurrentListSubsection(listSubsections[0] || null);
  }, [listSubsections]);

  const handleOpenModalFromReferencedList: Parameters<typeof ModalFormContent>[0]['onOpenModalFromReferencedList'] = (referencedField) => {
    setField(referencedField);
    if (referencedField.modalIndex !== undefined) {
      onEditField(referencedField.modalIndex);
    }
  };

  const handleSaveClick = useCallback<Parameters<typeof SaveButton>[0]['onClick']>(({
    accumulatedModalChanges,
    newValue,
  }) => {
    /**
     * canSaveToDB is used to handle new modal changes so we don't autosave list record
     * when we click on save button we set this to true to enable autosave for saving the changes done in modal
     * till this step the changes are not saved to DB
     * so we can safely apply nested modal changes to the current form context
     * and then save the changes to DB
     */
    currentFormContext.setCanSaveValueToDB(true);
    if (!isEnabled) {
      return;
    }
    currentFormContext.applyNestedModalChanges(accumulatedModalChanges);
    setReferencedListData(null);
    onSaveRef.current(newValue);
    onCloseRef.current();
  }, [
    isEnabled,
    rowFieldKey,
    currentFormContext,
  ]);

  const handleRemoveClick = useCallback<Parameters<typeof RemoveButton>[0]['onClick']>(() => {
    if (!isEnabled || !onRemoveRef.current) {
      return;
    }
    if (window.confirm('Are you sure you want to remove this item?')) {
      onRemoveRef.current(rowIndex);
      onCloseRef.current();
    }
  }, [isEnabled, rowIndex]);

  const handleClose = useCallback(() => {
    // we can remove the list item when we click on handleClose if it's a new list item
    const canRemoveListItem = modalFieldId === newListItemId;

    /**
     * Since canRemoveListItem would be required to run after it's set and value is updated in the context
     * we use setTimeout to ensure that the value is updated before we remove the item.
     *
     * @todo - this is a workaround, we should find a better way to handle this
     *        so that we don't need to use setTimeout.
     */
    setTimeout(() => {
      onClose(canRemoveListItem);
    }, 0);
    setReferencedListData(null);
  }, [currentFormContext.formMethods, modalFormMethods, onClose, setReferencedListData, rowFieldKey]);

  return (
    <ModalWrapper
      field={field}
      onClose={handleClose}
      rowIndex={rowIndex}
      listSubsections={listSubsections}
      referencedListData={referencedListData}
    >
      <QuestionnaireFormProvider
        formMethods={modalFormMethods}
        id={`Modal-Level-${parentModalLevel + 1}`}
      >
        {isEnabled && (
          <>
            <HandleWatchers
              isAutoSaveEnabled={false}
              isAutofillEnabled={isEnabled}
              isAiAgentsEnabled={isEnabled}
              isActionsEnabled={isEnabled}
              isDynamicOptionsEnabled={true}
              isSectionsConditionEnabled={false}
              formData={formData}
              onStartAgent={setFieldsAutoFilling}
              filterAiAgents={filterAiAgents}
              filterAutofills={filterAutofills}
              currentTableRowKey={rowFieldKey}
            />
            <TransferData
              fromContext={currentFormContext}
            />
          </>
        )}
        <ModalFormHeader
          currentListSubsection={currentListSubsection}
          field={field}
          linkSourceFieldKey={linkSourceFieldKey}
          listSubsections={listSubsections}
          modalSubFields={modalSubFields}
          onSelectSubsection={setCurrentListSubsection}
          rowIndex={rowIndex}
        />
        {isEnabled && (
          <ModalFormActions
            rowIndex={rowIndex}
            linkSourceFieldKey={linkSourceFieldKey}
          />
        )}
        <ModalFormContent
          currentListSubsection={currentListSubsection}
          field={field}
          fieldsAutofilling={fieldsAutofilling}
          FormFieldComponent={FormFieldComponent}
          onUpdateReferencedListData={setReferencedListData}
          linkSourceFieldKey={linkSourceFieldKey}
          listSubsections={listSubsections}
          modalSubFields={modalSubFields}
          onOpenModalFromReferencedList={handleOpenModalFromReferencedList}
          rowIndex={rowIndex}
        />
        {isEnabled && !field.disabled && (
          <div className={s.saveButton}>
            <SaveButton
              onClick={handleSaveClick}
              rowFieldKey={rowFieldKey}
            />
            {onRemove && (
              <RemoveButton
                onClick={handleRemoveClick}
              />
            )}
          </div>
        )}
      </QuestionnaireFormProvider>
    </ModalWrapper>
  );
};

export default EditListItemModalV2;
